import React, { useState, useEffect } from 'react';
import { ContactCollection } from '../types/Collections';
import { supabase } from '../lib/supabase';
import { listCollectionsCore, listMembershipsForCollectionsCore, bulkAddMembersCore, bulkRemoveMembersCore, createCollectionCore } from '@gv/core';
import { User } from '@supabase/supabase-js';
import {
    X,
    Plus,
    FolderPlus,
    Users,
    Check,
    Loader
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import NotificationToast from './NotificationToast';
import SquareLoader from './SquareLoader';

interface CollectionSelectorModalProps {
    selectedContactIds: string[];
    user: User;
    onClose: () => void;
    onContactsAdded: () => void;
}

export default function CollectionSelectorModal({
    selectedContactIds,
    user,
    onClose,
    onContactsAdded
}: CollectionSelectorModalProps) {
    const [collections, setCollections] = useState<ContactCollection[]>([]);
    const [loading, setLoading] = useState(true);
    // membershipCounts[collectionId] = number of selected contacts currently in that collection
    const [membershipCounts, setMembershipCounts] = useState<Record<string, number>>({});
    // selectionTargets[collectionId] = true (checked), false (unchecked), null/undefined (no change, derive from current)
    const [selectionTargets, setSelectionTargets] = useState<Record<string, boolean | null>>({});
    const [newCollectionName, setNewCollectionName] = useState('');
    const [creatingCollection, setCreatingCollection] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const { notification, showNotification, hideNotification } = useNotifications();

    useEffect(() => {
        fetchCollections();
    }, []);

    const fetchCollections = async () => {
        setLoading(true);
        try {
            const data = await listCollectionsCore(supabase, user.id);
            setCollections(data || []);
            // Load membership counts for currently selected contacts
            const ids = (data || []).map(c => c.id);
            if (ids.length > 0 && selectedContactIds.length > 0) {
                const rows = await listMembershipsForCollectionsCore(supabase, ids, selectedContactIds);
                const counts: Record<string, number> = {};
                (rows || []).forEach((r: any) => {
                    counts[r.collection_id] = (counts[r.collection_id] || 0) + 1;
                });
                setMembershipCounts(counts);
                // Reset selection targets on load
                setSelectionTargets({});
            } else {
                setMembershipCounts({});
                setSelectionTargets({});
            }
        } catch (error) {
            console.error('Error fetching collections:', error);
        } finally {
            setLoading(false);
        }
    };

    // Derived helpers
    const totalSelected = selectedContactIds.length;
    const stateFor = (collectionId: string) => {
        const cnt = membershipCounts[collectionId] || 0;
        const allIn = cnt === totalSelected && totalSelected > 0;
        const anyIn = cnt > 0;
        const target = selectionTargets[collectionId];
        const finalChecked = target === true ? true : target === false ? false : allIn ? true : false;
        const indeterminate = target == null && anyIn && !allIn;
        return { cnt, allIn, anyIn, target, finalChecked, indeterminate };
    };

    const toggleCollection = (collectionId: string) => {
        setSelectionTargets(prev => {
            const { allIn, target } = stateFor(collectionId);
            // cycle: null->true if not allIn, null->false if allIn; true->false; false->true
            if (target == null) {
                return { ...prev, [collectionId]: allIn ? false : true };
            }
            return { ...prev, [collectionId]: !target };
        });
    };

    const selectAll = () => {
        const next: Record<string, boolean> = {} as any;
        collections.forEach(c => { next[c.id] = true; });
        setSelectionTargets(next);
    };
    const clearAll = () => {
        const next: Record<string, boolean> = {} as any;
        collections.forEach(c => { next[c.id] = false; });
        setSelectionTargets(next);
    };

    const applyChanges = async () => {
        try {
            // Build operations
            const userCollectionIds = collections.map(c => c.id);
            const addOps: Array<{ collection_id: string; contact_id: string; added_by: string }> = [];
            const removeByCollection: Record<string, string[]> = {};

            let anyFinalChecked = false;
            for (const c of collections) {
                const { finalChecked } = stateFor(c.id);
                if (finalChecked) anyFinalChecked = true;
            }

            if (!anyFinalChecked) {
                // Remove selected contacts from all collections
                await bulkRemoveMembersCore(supabase, Object.fromEntries(userCollectionIds.map(id => [id, selectedContactIds])));
                showNotification(`Removed ${selectedContactIds.length} contact${selectedContactIds.length !== 1 ? 's' : ''} from all collections`, 'success');
                // Notify other views to refresh
                try { window.dispatchEvent(new Event('gv:refreshCollectionMembers')); } catch { }
                onContactsAdded();
                return;
            }

            // Load existing memberships to compute diffs
            const existingRows = await listMembershipsForCollectionsCore(supabase, userCollectionIds, selectedContactIds);
            const existingSet = new Set((existingRows || []).map((r: any) => `${r.collection_id}:${r.contact_id}`));

            for (const c of collections) {
                const { finalChecked } = stateFor(c.id);
                if (finalChecked) {
                    // ensure all selectedContactIds exist
                    for (const contactId of selectedContactIds) {
                        const key = `${c.id}:${contactId}`;
                        if (!existingSet.has(key)) {
                            addOps.push({ collection_id: c.id, contact_id: contactId, added_by: user.id });
                        }
                    }
                } else {
                    // remove any existing for this collection
                    for (const contactId of selectedContactIds) {
                        const key = `${c.id}:${contactId}`;
                        if (existingSet.has(key)) {
                            if (!removeByCollection[c.id]) removeByCollection[c.id] = [];
                            removeByCollection[c.id].push(contactId);
                        }
                    }
                }
            }

            let added = 0;
            let removed = 0;
            if (addOps.length > 0) {
                added = await bulkAddMembersCore(supabase, addOps);
            }
            removed = await bulkRemoveMembersCore(supabase, removeByCollection);

            showNotification(
                `${added > 0 ? `Added ${added}` : ''}${added > 0 && removed > 0 ? ' • ' : ''}${removed > 0 ? `Removed ${removed}` : ''}` || 'No changes',
                'success'
            );
            // Notify other views to refresh
            try { window.dispatchEvent(new Event('gv:refreshCollectionMembers')); } catch { }
            onContactsAdded();
        } catch (error) {
            console.error('Error applying collection changes:', error);
            showNotification('Failed to update collections. Please try again.', 'error');
        }
    };

    const createCollection = async () => {
        if (!newCollectionName.trim()) {
            showNotification('Please enter a collection name', 'error');
            return;
        }

        setCreatingCollection(true);
        try {
            const data = await createCollectionCore(supabase, user.id, { name: newCollectionName.trim(), color: '#3B82F6' });

            // Add contacts to the new collection
            await bulkAddMembersCore(supabase, selectedContactIds.map(contactId => ({ collection_id: data.id, contact_id: contactId, added_by: user.id })));

            showNotification(`Created "${newCollectionName}" and added ${selectedContactIds.length} contact${selectedContactIds.length !== 1 ? 's' : ''}`, 'success');
            try { window.dispatchEvent(new Event('gv:refreshCollectionMembers')); } catch { }
            onContactsAdded();
        } catch (error) {
            console.error('Error creating collection:', error);
            showNotification('Failed to create collection', 'error');
        } finally {
            setCreatingCollection(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && newCollectionName.trim()) {
            createCollection();
        } else if (e.key === 'Escape') {
            if (showCreateForm) {
                setShowCreateForm(false);
                setNewCollectionName('');
            } else {
                onClose();
            }
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-black border border-gray-800 p-8 rounded-lg">
                    <div className="flex items-center space-x-3">
                        <span className="text-white"><SquareLoader size="sm" /></span>
                        <span className="text-white">Loading collections...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-black border border-gray-800 w-full max-w-md max-h-[90vh] overflow-y-auto">
                {notification && (
                    <NotificationToast notification={notification} onClose={hideNotification} />
                )}
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div>
                        <h2 className="text-xl font-light text-white tracking-wide">
                            Add to Collection
                        </h2>
                        <p className="text-gray-400 text-sm">
                            {selectedContactIds.length} contact{selectedContactIds.length !== 1 ? 's' : ''} selected
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Create New Collection */}
                    <div className="mb-6">
                        {!showCreateForm ? (
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="w-full flex items-center justify-center px-4 py-3 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 transition-colors"
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                Create New Collection
                            </button>
                        ) : (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={newCollectionName}
                                    onChange={(e) => setNewCollectionName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter collection name..."
                                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    autoFocus
                                />
                                <div className="flex space-x-2">
                                    <button
                                        onClick={createCollection}
                                        disabled={!newCollectionName.trim() || creatingCollection}
                                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-white text-black hover:bg-gray-100 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {creatingCollection ? (
                                            <>
                                                <span className="mr-2 text-black"><SquareLoader size="xs" /></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <FolderPlus className="w-4 h-4 mr-2" />
                                                Create & Add
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setNewCollectionName('');
                                        }}
                                        className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Existing Collections with tri-state selection */}
                    {collections.length > 0 ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-300">
                                    Existing Collections
                                </h3>
                                <div className="flex items-center gap-2">
                                    <button onClick={selectAll} className="text-xs text-blue-400 hover:text-blue-300 underline">Select all</button>
                                    <button onClick={clearAll} className="text-xs text-gray-400 hover:text-white underline">Clear all</button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {collections.map(collection => {
                                    const { finalChecked, indeterminate, cnt } = stateFor(collection.id);
                                    return (
                                        <label
                                            key={collection.id}
                                            className="w-full flex items-center justify-between p-3 bg-gray-900 border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-colors text-left cursor-pointer"
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    ref={(el) => { if (el) el.indeterminate = indeterminate; }}
                                                    type="checkbox"
                                                    checked={finalChecked}
                                                    onChange={() => toggleCollection(collection.id)}
                                                    className="w-4 h-4 accent-white"
                                                />
                                                <div>
                                                    <p className="text-white font-medium">{collection.name}</p>
                                                    {(collection as any).message_context && (
                                                        <p className="text-gray-400 text-xs line-clamp-2">{(collection as any).message_context}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {cnt || 0}/{totalSelected}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-400">
                            <FolderPlus className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                            <p className="mb-4">No collections yet</p>
                            <p className="text-sm">Create your first collection above</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-800">
                    <div className="text-xs text-gray-400">
                        Tip: If no collections are selected, selected contacts will be removed from all collections.
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={applyChanges}
                            className="px-6 py-2 bg-white text-black hover:bg-gray-100 transition-colors font-medium"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
